import { useEffect, useState } from "react";
import { BadgeCheck, BellRing, Download, FileDown, FileSpreadsheet, FileText, Stethoscope } from "lucide-react";
import { StatoPraticaDialog } from "./StatoPraticaDialog";
import { CmsManager } from "./CmsManager";
import { toast } from "sonner";
import { adminApi } from "../../pages/Segreteria";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "—");
const fmtDay = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT") : "—");
const giorniA = (iso) => Math.ceil((new Date(iso) - Date.now()) / 86400000);

const STATO = {
  pagato: "bg-green-900/40 text-green-400 border-green-800",
  tessera_emessa: "bg-red-600 text-white border-red-600",
  in_attesa_pagamento: "bg-neutral-800 text-neutral-300 border-neutral-700",
};
const PAID = new Set(["pagato", "tessera_emessa"]);

const Stat = ({ label, value, testid }) => (
  <div data-testid={testid} className="border border-neutral-800 bg-neutral-900 p-5">
    <div className="mono-label text-[10px] uppercase tracking-wider text-neutral-500">{label}</div>
    <div className="font-display text-3xl font-black text-white mt-1">{value}</div>
  </div>
);

const FileBtn = ({ id, file, label, icon: Icon, testid, href }) => (
  <a
    data-testid={testid}
    href={href || `${API}/admin/tesseramenti/${id}/file/${file}`}
    target="_blank"
    rel="noopener noreferrer"
    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-neutral-700 hover:border-red-600 text-neutral-200 hover:text-red-500 mono-label text-[10px] uppercase tracking-wider transition-colors"
  >
    <Icon className="w-3.5 h-3.5" /> {label}
  </a>
);

export const AdminDashboard = () => {
  const [tab, setTab] = useState("tesseramenti");
  const [stats, setStats] = useState(null);
  const [tess, setTess] = useState([]);
  const [contatti, setContatti] = useState([]);
  const [sending, setSending] = useState(false);
  const [editing, setEditing] = useState(null);

  const inviaPromemoria = async () => {
    setSending(true);
    try {
      const { data } = await adminApi.post("/admin/rinnovi/invia");
      toast.success(`Promemoria inviati: ${data.inviati} (controllati ${data.controllati}, errori ${data.errori})`);
      adminApi.get("/admin/tesseramenti").then((r) => setTess(r.data)).catch(() => {});
    } catch (e) {
      toast.error("Invio promemoria non riuscito");
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    adminApi.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    adminApi.get("/admin/tesseramenti").then((r) => setTess(r.data)).catch(() => {});
    adminApi.get("/admin/contatti").then((r) => setContatti(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="admin-dashboard" className="max-w-7xl mx-auto px-4 sm:px-8 py-10 space-y-8">
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        <Stat testid="stat-richieste" label="Richieste dal sito" value={stats?.richieste ?? "—"} />
        <Stat testid="stat-tesseramenti" label="Tesseramenti" value={stats?.tesseramenti ?? "—"} />
        <Stat testid="stat-pagati" label="Pagati" value={stats?.tesseramenti_pagati ?? "—"} />
        <Stat testid="stat-emesse" label="Tessere emesse" value={stats?.tessere_emesse ?? "—"} />
        <Stat testid="stat-incasso" label="Incasso" value={stats ? `${stats.incasso.toFixed(0)} €` : "—"} />
        <Stat testid="stat-scadenza" label="In scadenza (30 gg)" value={stats?.in_scadenza_30 ?? "—"} />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="mono-label text-xs uppercase tracking-wider text-neutral-500">Report MSP / CONI</span>
        <a
          data-testid="export-excel"
          href={`${API}/admin/export.xlsx`}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-white hover:bg-red-600 text-black hover:text-white mono-label text-[11px] font-black uppercase tracking-wider transition-colors"
        >
          <FileSpreadsheet className="w-4 h-4" /> Esporta Excel (soci + richieste)
        </a>
      </div>

      <div data-testid="rinnovi-box" className="flex flex-wrap items-center justify-between gap-4 border border-neutral-800 bg-neutral-900 p-4">
        <div className="text-xs text-neutral-400">
          <span className="mono-label text-[10px] uppercase tracking-wider text-red-500 block">Rinnovo annuale automatico</span>
          Ogni giorno alle 8:00 i soci ricevono un'email di promemoria 30 e 7 giorni prima della scadenza e il giorno stesso (la segreteria è avvisata alla scadenza).
        </div>
        <button
          type="button"
          data-testid="rinnovi-invia"
          onClick={inviaPromemoria}
          disabled={sending}
          className="inline-flex items-center gap-2 px-4 py-2 border border-neutral-700 hover:border-red-600 text-neutral-200 hover:text-red-500 disabled:opacity-50 mono-label text-[11px] font-bold uppercase tracking-wider transition-colors"
        >
          <BellRing className="w-4 h-4" /> {sending ? "Invio..." : "Invia promemoria di oggi"}
        </button>
      </div>

      <div className="flex gap-2 border-b border-neutral-800">
        {[["tesseramenti", `Tesseramenti (${tess.length})`], ["richieste", `Richieste (${contatti.length})`], ["eventi", "Pagine gare"], ["interviste", "Video interviste"]].map(([k, l]) => (
          <button
            key={k}
            type="button"
            data-testid={`admin-tab-${k}`}
            onClick={() => setTab(k)}
            className={`px-5 py-3 mono-label text-xs uppercase tracking-wider border-b-2 -mb-px transition-colors ${
              tab === k ? "border-red-600 text-white" : "border-transparent text-neutral-500 hover:text-neutral-200"
            }`}
          >
            {l}
          </button>
        ))}
      </div>

      {tab === "tesseramenti" && (
        <div data-testid="admin-tesseramenti" className="overflow-x-auto border border-neutral-800">
          <table className="w-full text-left text-xs">
            <thead className="bg-neutral-900 mono-label text-[10px] uppercase tracking-wider text-neutral-500">
              <tr>
                {["Data", "Socio", "Contatti", "Tessera", "Stato", "Scadenza", "Documenti"].map((h) => (
                  <th key={h} className="px-4 py-3 font-normal">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-900">
              {tess.map((t) => (
                <tr key={t.id} data-testid={`tess-row-${t.id}`} className="hover:bg-neutral-900/60">
                  <td className="px-4 py-3 whitespace-nowrap text-neutral-400">{fmtDate(t.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="font-bold text-white uppercase">{t.cognome} {t.nome}</div>
                    <div className="text-neutral-500">{t.indirizzo}</div>
                  </td>
                  <td className="px-4 py-3 text-neutral-300">
                    <div>{t.email}</div>
                    <div className="text-neutral-500">{t.cellulare}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-white font-bold">{t.tessera_label} — {t.prezzo.toFixed(0)} €</div>
                    <div className="text-neutral-500">{t.gruppo_label}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-1 border mono-label text-[10px] uppercase ${STATO[t.stato] || STATO.in_attesa_pagamento}`}>
                      {t.stato.replaceAll("_", " ")}
                    </span>
                    {t.codice_coni && (
                      <div data-testid={`coni-${t.id}`} className="mt-1 mono-label text-[10px] text-neutral-300">CONI {t.codice_coni}</div>
                    )}
                    {PAID.has(t.stato) && (
                      <button
                        type="button"
                        data-testid={`stato-edit-${t.id}`}
                        onClick={() => setEditing(t)}
                        className="mt-1.5 inline-flex items-center gap-1 mono-label text-[10px] uppercase text-red-500 hover:text-red-400"
                      >
                        <BadgeCheck className="w-3 h-3" /> {t.stato === "tessera_emessa" ? "Modifica" : "Segna emessa"}
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap" data-testid={`scadenza-${t.id}`}>
                    {t.scadenza ? (
                      <>
                        <div className={giorniA(t.scadenza) <= 30 ? "text-amber-400 font-bold" : "text-neutral-300"}>{fmtDay(t.scadenza)}</div>
                        <div className="text-neutral-500">
                          {giorniA(t.scadenza) < 0 ? "scaduta" : `${giorniA(t.scadenza)} gg`}
                          {t.promemoria_inviati?.length ? ` · avvisi: ${t.promemoria_inviati.join("/")}` : ""}
                        </div>
                      </>
                    ) : <span className="text-neutral-600">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-2">
                      <FileBtn id={t.id} file="documento" label="Documento" icon={FileText} testid={`dl-doc-${t.id}`} />
                      {t.ha_certificato && (
                        <FileBtn id={t.id} file="certificato" label="Certificato" icon={Stethoscope} testid={`dl-cert-${t.id}`} />
                      )}
                      {PAID.has(t.stato) && (
                        <FileBtn id={t.id} label="Ricevuta PDF" icon={FileDown} testid={`dl-ricevuta-${t.id}`} href={`${API}/admin/tesseramenti/${t.id}/ricevuta`} />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {tess.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-neutral-500">Nessun tesseramento.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <StatoPraticaDialog
          tess={editing}
          onClose={() => setEditing(null)}
          onSaved={(data) => {
            setTess((rows) => rows.map((r) => (r.id === data.id ? { ...r, ...data, note_segreteria: data.note_segreteria } : r)));
            adminApi.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
          }}
        />
      )}

      {tab === "eventi" && <CmsManager kind="eventi" />}
      {tab === "interviste" && <CmsManager kind="interviste" />}

      {tab === "richieste" && (
        <div data-testid="admin-richieste" className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {contatti.map((c) => (
            <article key={c.id} data-testid={`contact-row-${c.id}`} className="border border-neutral-800 bg-neutral-900 p-5">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="mono-label text-[10px] uppercase tracking-wider text-red-500">{c.tipo}</span>
                <span className="mono-label text-[10px] text-neutral-500">{fmtDate(c.timestamp)}</span>
              </div>
              <div className="font-bold text-white uppercase mt-1">{c.nome}</div>
              <a href={`mailto:${c.email}`} className="text-xs text-neutral-400 hover:text-red-500">{c.email}</a>
              <p className="text-sm text-neutral-200 mt-3 whitespace-pre-line leading-relaxed">{c.richiesta}</p>
              {c.email_inviata === false && (
                <span className="inline-flex items-center gap-1 mt-3 mono-label text-[10px] uppercase text-amber-400"><Download className="w-3 h-3" /> email non inviata</span>
              )}
            </article>
          ))}
          {contatti.length === 0 && <p className="text-neutral-500 text-sm">Nessuna richiesta.</p>}
        </div>
      )}
    </div>
  );
};
